import { useEffect, useRef, useState } from 'react';
import {
  useAppSelector,
  useAppDispatch,
  markStepCompleted,
  AppRoutes,
  type PlatformType,
} from '@core';
import { createOrderAndGetPaymentUrl } from './store/actions';
import { useForm } from 'react-hook-form';
import type { CheckoutFormData } from './models/checkoutFormData.interfaces';
import { OrderSummary } from './components/OrderSummary';
import { FormField } from './components/FormField';
import { PromoCodeField } from './components/PromoCodeField';
import { CHECKOUT_VALIDATION, ALLOWED_CHARS_PATTERNS } from './validation/checkout.validation';
import { TermsAgreement } from './components/TermsAgreement';
import { toast } from 'sonner';
import {
  urlParamsDataSelectors,
  selectedSeatsSelector,
  selectedSeatIdsSelector,
} from '../seatSelection/store/selectors';
import { CheckoutMiniFooter } from './components/CheckoutMiniFooter';
import { getValidDateTime } from './store/selectors';
import { useNavigate } from 'react-router-dom';
import { BookingTimerContainer } from '../../shared/BookingTimerContainer/BookingTimerContainer';
import { TimeoutModal } from '@shared/TimeoutModal/TimeoutModal';
import { useTimeoutHandler } from '@hooks/useTimeoutHandler';
import { InsuranceCard } from './components/InsuranceCard';
import { selectedDateSelectors, showInfoSelectors } from '../dateSelection/store/selectors';
import { useCheckoutCalculation } from './hooks/useCheckoutCalculation';
import { formatSectorWithVisibility } from '../seatSelection/utils/formatSeatVisibility';

interface CheckoutProps {
  platformType: PlatformType;
}

export const Checkout = ({ platformType }: CheckoutProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const urlParamsData = useAppSelector(urlParamsDataSelectors);
  const selectedSeats = useAppSelector(selectedSeatsSelector);
  const selectedSeatIds = useAppSelector(selectedSeatIdsSelector);
  const selectedDate = useAppSelector(selectedDateSelectors);
  const showInfo = useAppSelector(showInfoSelectors);
  const validDateTime = useAppSelector(getValidDateTime);
  const showId = urlParamsData.showIds;
  const eventId = selectedDate?.eventId;
  const totalPrice = selectedSeats.reduce((sum, seat) => sum + (seat.price || 0), 0);
  const {
    promoCode,
    calculation,
    priceView,
    isInsuranceSelected,
    handlePromoCodeChange,
    handleApplyPromoCode,
    handleInsuranceToggle,
  } = useCheckoutCalculation({
    showId,
    eventId,
    selectedSeatIds,
    initialTicketsPrice: totalPrice,
    insuranceAvailable: showInfo?.insuranceAvailable === true,
  });
  const {
    ticketsPrice,
    discountAmount,
    serviceFee,
    insuranceAmount,
    insurancePremium,
    isInsuranceAvailable,
    hasInsurance,
    totalAmount: displayedTotalAmount,
  } = priceView;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { handleTimeout, isModalOpen, closeModal, timeoutPhase } = useTimeoutHandler({
    validDateTime,
  });
  const paymentWindowRef = useRef<Window | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CheckoutFormData>();

  const handleProceedToPayment = async (formData: CheckoutFormData) => {
    if (!selectedSeats.length) {
      toast.error('Нет выбранных мест для оформления заказа');
      return;
    }
    if (!showId) {
      toast.error('Не удалось определить шоу');
      return;
    }
    if (!eventId) {
      toast.error('Не удалось определить сеанс');
      return;
    }

    const selectedEventIds = new Set(selectedSeats.map(seat => seat.eventId));
    if (selectedEventIds.size !== 1 || !selectedEventIds.has(eventId)) {
      toast.error('Выбранные места относятся к разным сеансам');
      return;
    }

    const pendingWindow = window.open('about:blank', '_blank');
    if (pendingWindow) {
      paymentWindowRef.current = pendingWindow;
      pendingWindow.document.write(
        '<p style="font-family: sans-serif; padding: 16px;">Перенаправляем на оплату...</p>'
      );
      pendingWindow.focus();
    }

    try {
      setIsSubmitting(true);
      // Используем комбинированный action для создания заказа и получения URL оплаты
      const paymentLink = await dispatch(
        createOrderAndGetPaymentUrl({
          formData: {
            ...formData,
            promoCode: promoCode.appliedValue ?? undefined,
          },
          showId,
          eventId,
          platformType,
          insurance: hasInsurance,
          seatIds: selectedSeatIds,
        })
      ).unwrap();
      if (paymentLink) {
        if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
          paymentWindowRef.current.location.href = paymentLink;
          paymentWindowRef.current.focus();
        } else {
          const openedWindow = window.open(paymentLink, '_blank');
          paymentWindowRef.current = openedWindow || null;
          openedWindow?.focus();
        }
      }
      dispatch(markStepCompleted(AppRoutes.CHECKOUT));
    } catch (error: any) {
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
      paymentWindowRef.current = null;
      const errorMessage = error.message || 'Произошла ошибка при оформлении заказа';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
      paymentWindowRef.current = null;
    };
  }, []);

  return (
    <>
      <BookingTimerContainer
        validDateTime={validDateTime}
        isSubmitting={isSubmitting}
        onTimeout={handleTimeout}
      />
      <div className="mx-auto w-full max-w-6xl">
        <h1 className="text-xl font-bold mb-2  text-center">Оформление заказа</h1>

        <div className="mb-6 rounded-lg border border-separator bg-surface-card p-4">
          <h2 className="mb-3 text-base font-semibold text-text-primary">Выбранные места</h2>
          <div className="space-y-3">
            {selectedSeats.map(seat => (
              <div
                key={seat.seatId}
                data-selected-seat-id={seat.seatId}
                className="flex items-start justify-between gap-4 border-b border-separator pb-3 last:border-b-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">
                    Ряд {seat.rowName}, Место {seat.seatNumber}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {formatSectorWithVisibility(seat.sectorName, seat.visibility)}
                  </p>
                </div>
                <p className="shrink-0 text-sm font-medium text-action-primary">
                  {seat.price.toLocaleString()} ₽
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <form onSubmit={handleSubmit(handleProceedToPayment)}>
            <div className="space-y-3">
              <FormField
                id="lastName"
                label="Фамилия*"
                register={register}
                validation={CHECKOUT_VALIDATION.lastName}
                errors={errors}
                allowedCharsPattern={ALLOWED_CHARS_PATTERNS.name}
              />

              <FormField
                id="firstName"
                label="Имя*"
                register={register}
                validation={CHECKOUT_VALIDATION.firstName}
                errors={errors}
                allowedCharsPattern={ALLOWED_CHARS_PATTERNS.name}
              />

              <FormField
                id="phone"
                label="Номер телефона*"
                type="tel"
                placeholder="Введите номер телефона"
                register={register}
                validation={CHECKOUT_VALIDATION.phone}
                errors={errors}
                allowedCharsPattern={ALLOWED_CHARS_PATTERNS.phone}
              />

              <FormField
                id="email"
                label="Email*"
                type="email"
                register={register}
                validation={CHECKOUT_VALIDATION.email}
                errors={errors}
              />

              <PromoCodeField
                value={promoCode.value}
                isLoading={promoCode.isLoading}
                error={promoCode.error}
                isValid={promoCode.isValid}
                response={calculation.response}
                onPromoCodeChange={handlePromoCodeChange}
                onApplyPromoCode={handleApplyPromoCode}
              />

              {isInsuranceAvailable && (
                <div className="pt-1">
                  <InsuranceCard
                    premium={insuranceAmount}
                    selected={isInsuranceSelected}
                    isLoading={calculation.isLoading}
                    onSelect={handleInsuranceToggle}
                  />
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Информация о заказе */}
        <OrderSummary
          isSubmitting={isSubmitting}
          totalPrice={ticketsPrice}
          serviceFee={serviceFee}
          discountAmount={discountAmount}
          totalAmount={displayedTotalAmount}
          insurancePremium={insurancePremium}
          isInsuranceSelected={hasInsurance}
          isCalculating={calculation.isLoading}
          validDateTime={validDateTime}
          onProceedToPayment={handleSubmit(handleProceedToPayment)}
        />
        {/* Текст соглашения с условиями */}
        <TermsAgreement />
        {/* Мини-футер со ссылками */}
        <CheckoutMiniFooter showId={showId} />

        <TimeoutModal
          open={isModalOpen}
          type={timeoutPhase || 'payment'}
          onConfirm={() => {
            navigate(AppRoutes.SEATS_SELECTION);
            closeModal();
          }}
        />
      </div>
    </>
  );
};
